import {
  type GetDocRolePermissionsQuery,
  getDocRolePermissionsQuery,
  type GetWorkspaceInfoQuery,
  getWorkspaceInfoQuery,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import type { WorkspaceService } from '../../workspace';

export type WorkspacePermissionActions = keyof Omit<
  GetWorkspaceInfoQuery['workspace']['permissions'],
  '__typename'
>;

export type DocPermissionActions = keyof Omit<
  GetDocRolePermissionsQuery['workspace']['doc']['permissions'],
  '__typename'
>;

/** Every doc permission explicitly denied — the answer for a hidden doc. */
const NO_DOC_PERMISSIONS: Record<DocPermissionActions, boolean> = {
  Doc_Copy: false,
  Doc_Delete: false,
  Doc_Duplicate: false,
  Doc_Properties_Read: false,
  Doc_Properties_Update: false,
  Doc_Publish: false,
  Doc_Read: false,
  Doc_Restore: false,
  Doc_TransferOwner: false,
  Doc_Trash: false,
  Doc_Update: false,
  Doc_Users_Manage: false,
  Doc_Users_Read: false,
  Doc_Comments_Create: false,
  Doc_Comments_Delete: false,
  Doc_Comments_Read: false,
  Doc_Comments_Resolve: false,
};

export class GuardStore extends Store {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  async getWorkspacePermissions(): Promise<
    Record<WorkspacePermissionActions, boolean>
  > {
    if (!this.workspaceServerService.server) {
      throw new Error('No server');
    }
    const data = await this.workspaceServerService.server.gql({
      query: getWorkspaceInfoQuery,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
      },
    });
    return data.workspace.permissions;
  }

  async getDocPermissions(
    docId: string
  ): Promise<Record<DocPermissionActions, boolean>> {
    if (!this.workspaceServerService.server) {
      throw new Error('No server');
    }
    try {
      const data = await this.workspaceServerService.server.gql({
        query: getDocRolePermissionsQuery,
        variables: {
          workspaceId: this.workspaceService.workspace.id,
          docId,
        },
      });
      return data.workspace.doc.permissions;
    } catch (err) {
      // Hide-by-default access model: for a doc the user cannot read, the
      // server answers DOC_ACTION_DENIED instead of a permissions object.
      // Treat it as an explicit all-false answer — otherwise the guard's
      // backoff retries this 403 forever and `can$` never resolves (doc lists
      // and the detail page would show loading instead of "no access").
      if (String(err).includes('DOC_ACTION_DENIED')) {
        return NO_DOC_PERMISSIONS;
      }
      throw err;
    }
  }
}
